import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  ProgressBar,
} from '@/components/ui';
import { AccountActions } from '@/features/admin/AccountActions';
import { SignInAsAction } from '@/features/admin/SignInAsAction';
import { StudentCertificationsCard } from '@/features/admin/StudentCertificationsCard';
import { StudentCvsCard } from '@/features/admin/StudentCvsCard';
import { VerifyEmailAction } from '@/features/admin/VerifyEmailAction';
import { Fact, FactList, Prose } from '@/features/admin/Facts';
import { RecordHeader } from '@/features/admin/RecordHeader';
// Identity verification is switched off for now.
// import { StudentIdentityActions } from '@/features/admin/StudentIdentityActions';
import { ACCOUNT_TONES, humanizeStatus } from '@/features/admin/status';
import { CustomerInvoices } from '@/features/billing/CustomerInvoices';
import { ADMIN_DOCUMENT_LABELS, DOCUMENT_TONES } from '@/features/verification/status';
import { StudentPlanPanel } from '@/features/billing/StudentPlanPanel';
import { APPLICATION_LABELS, APPLICATION_TONES } from '@/features/applications/status';
import { INTERVIEW_LABELS, INTERVIEW_TONES } from '@/features/interviews/status';
import { OFFER_LABELS, OFFER_TONES } from '@/features/offers/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate, formatMonthYear, formatStipend, humanizeWorkMode } from '@/utils/format';
import { formatPhone } from '@/utils/phone';

function dateRange(from, to, isCurrent) {
  return `${formatMonthYear(from)} — ${isCurrent ? 'present' : formatMonthYear(to)}`;
}

/**
 * One student, whole.
 *
 * A superset of what a company sees on a candidate: no contact details are
 * withheld here, because an admin looking at a profile is usually answering a
 * support or abuse question and cannot do that through a redacted view.
 */
export function AdminStudentDetailPage() {
  const { id } = useParams();
  return <AdminStudentRecord studentId={id} />;
}

/**
 * The whole student record for one id. `embedded` drops the way back to the
 * students list, for when the record is a tab inside another record (an
 * application) rather than a page of its own.
 */
export function AdminStudentRecord({ studentId: id, embedded = false }) {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['admin-student', id],
    queryFn: () => adminApi.getStudent(id),
  });

  if (isPending) return <LoadingState label="Loading the student…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const links = [data.portfolio_url, data.linkedin_url, data.github_url].filter(Boolean);
  const pipeline = Object.entries(data.applications_by_status ?? {}).filter(
    ([, count]) => count > 0,
  );

  return (
    <div>
      <RecordHeader
        backTo={embedded ? undefined : paths.admin.students}
        backLabel="Students"
        eyebrow="Student"
        title={data.full_name}
        subtitle={
          data.headline ||
          [data.university, data.degree].filter(Boolean).join(' · ') ||
          'No headline yet.'
        }
        badges={
          <>
            <Badge tone={ACCOUNT_TONES[data.account_status] ?? 'neutral'}>
              {humanizeStatus(data.account_status)}
            </Badge>
            {/* Identity verification is switched off for now.
            {data.identity_verification_status === 'VERIFIED' && (
              <Badge tone="success">Identity verified</Badge>
            )}
            {data.identity_verification_status === 'REJECTED' && (
              <Badge tone="danger">Identity refused</Badge>
            )} */}
            {data.open_to_opportunities ? (
              <Badge tone="brand">Visible to companies</Badge>
            ) : (
              <Badge tone="neutral">Hidden from companies</Badge>
            )}
            {data.university_email_verified && <Badge tone="success">Student email verified</Badge>}
            {!data.is_email_verified && <Badge tone="warning">Email unverified</Badge>}
          </>
        }
        actions={
          <>
            {/* Identity verification is switched off for now; restore the line
                below (and its import) to bring "Verify identity" back.
            <StudentIdentityActions student={data} /> */}
            <AccountActions
              account={{
                id: data.user_id,
                full_name: data.full_name,
                status: data.account_status,
                role: 'STUDENT',
              }}
            />
            <VerifyEmailAction
              account={{
                id: data.user_id,
                full_name: data.full_name,
                status: data.account_status,
                is_email_verified: data.is_email_verified,
                role: 'STUDENT',
              }}
            />
            <SignInAsAction
              account={{
                id: data.user_id,
                full_name: data.full_name,
                status: data.account_status,
                role: 'STUDENT',
              }}
            />
          </>
        }
        facts={[
          { label: 'Applications', value: data.application_count },
          { label: 'Skills', value: data.skills.length },
          { label: 'CVs', value: data.resumes.length },
          {
            label: 'Joined',
            value: formatDate(data.created_at),
            hint: data.last_login_at
              ? `Last seen ${formatDate(data.last_login_at)}`
              : 'Never signed in',
          },
        ]}
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader title="Profile" />
            <FactList>
              <Fact label="University" value={data.university} />
              <Fact label="University email" value={data.university_email} />
              <Fact label="Degree" value={data.degree} />
              <Fact label="Field of study" value={data.field_of_study} />
              <Fact label="Graduating" value={data.graduation_year} />
              <Fact label="Location" value={data.location} />
              <Fact
                label="Date of birth"
                value={data.date_of_birth ? formatDate(data.date_of_birth) : null}
              />
              {/* Identity verification is switched off for now.
              <Fact
                label="Identity check"
                value={humanizeStatus(data.identity_verification_status)}
              /> */}
              <Fact
                label="Prefers"
                value={data.preferred_work_mode ? humanizeWorkMode(data.preferred_work_mode) : null}
              />
              <Fact label="Preferred location" value={data.preferred_location} />
              <Fact
                label="Available from"
                value={data.available_from ? formatDate(data.available_from) : null}
              />
              <Fact
                label="Expected stipend"
                value={
                  data.expected_stipend_min != null
                    ? `${formatStipend(data.expected_stipend_min)} and up`
                    : null
                }
              />
              <Fact label="Target role" value={data.target_job_category} />
            </FactList>

            <Prose>{data.bio}</Prose>
            <Prose title="Career goal">{data.career_goal}</Prose>

            <ProgressBar
              className="mt-4"
              value={data.profile_completion}
              label="Profile completion"
            />
          </Card>

          <StudentCvsCard student={data} />

          {data.skills.length > 0 && (
            <Card>
              <CardHeader title="Skills" />
              <div className="flex flex-wrap gap-1.5">
                {data.skills.map((skill) => (
                  <Badge key={skill.id} tone="neutral">
                    {skill.name}
                    <span className="ml-1 opacity-70">{skill.proficiency.toLowerCase()}</span>
                  </Badge>
                ))}
              </div>
            </Card>
          )}

          {data.experience.length > 0 && (
            <Card>
              <CardHeader title="Experience" />
              <ol className="space-y-4">
                {data.experience.map((row) => (
                  <li key={row.id}>
                    <p className="text-ink font-medium">{row.title}</p>
                    <p className="text-ink-muted text-sm">
                      {[row.company_name, row.location].filter(Boolean).join(' · ')}
                    </p>
                    <p className="text-ink-muted text-xs">
                      {dateRange(row.start_date, row.end_date, row.is_current)}
                    </p>
                  </li>
                ))}
              </ol>
            </Card>
          )}

          {data.education.length > 0 && (
            <Card>
              <CardHeader title="Education" />
              <ol className="space-y-4">
                {data.education.map((row) => (
                  <li key={row.id}>
                    <p className="text-ink font-medium">
                      {row.degree}
                      {row.diploma_file_name && (
                        <Badge
                          dot
                          tone={DOCUMENT_TONES[row.diploma_status] ?? 'neutral'}
                          className="ml-2"
                        >
                          Certificate:{' '}
                          {ADMIN_DOCUMENT_LABELS[row.diploma_status] ?? row.diploma_status}
                        </Badge>
                      )}
                    </p>
                    <p className="text-ink-muted text-sm">
                      {[row.institution_name, row.field_of_study, row.grade]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                    <p className="text-ink-muted text-xs">
                      {dateRange(row.start_date, row.end_date, row.is_current)}
                    </p>
                  </li>
                ))}
              </ol>
            </Card>
          )}

          {data.certifications.length > 0 && <StudentCertificationsCard student={data} />}

          {data.portfolios.length > 0 && (
            <Card>
              <CardHeader title="Portfolio" />
              <ol className="space-y-3">
                {data.portfolios.map((row) => (
                  <li key={row.id}>
                    <p className="text-ink text-sm font-medium">{row.title}</p>
                    {row.description && <p className="text-ink-muted text-sm">{row.description}</p>}
                    <div className="flex flex-col gap-0.5">
                      {[row.project_url, row.repository_url].filter(Boolean).map((href) => (
                        <a
                          key={href}
                          href={href}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="text-brand-700 text-xs break-all hover:underline"
                        >
                          {href}
                        </a>
                      ))}
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          )}

          {/* The whole record, not the latest few: every application, round
              and offer, each opening the application it belongs to. */}
          <ApplicationHistory studentId={data.id} />
          <InterviewHistory studentId={data.id} />
          <OfferHistory studentId={data.id} />

          <CustomerInvoices studentId={data.id} />
        </div>

        <div className="space-y-6">
          <StudentPlanPanel studentId={data.id} studentName={data.full_name} />

          <Card>
            <CardHeader title="Contact" />
            <p className="text-ink text-sm break-all">{data.email}</p>
            {data.phone && <p className="text-ink-muted text-sm">{formatPhone(data.phone)}</p>}
            {links.length > 0 && (
              <div className="mt-2 flex flex-col gap-1 text-sm">
                {links.map((href) => (
                  <a
                    key={href}
                    href={href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-brand-700 break-all hover:underline"
                  >
                    {href}
                  </a>
                ))}
              </div>
            )}
          </Card>

          {pipeline.length > 0 && (
            <Card>
              <CardHeader title="Outcomes" />
              <dl className="space-y-1.5 text-sm">
                {pipeline.map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between gap-3">
                    <dt className="text-ink-muted">
                      {APPLICATION_LABELS[status] ?? humanizeStatus(status)}
                    </dt>
                    <dd className="text-ink font-medium tabular-nums">{count}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

const HISTORY_PAGE_SIZE = 10;

/**
 * One page of this student's recruitment history, read from the platform-wide
 * admin lists filtered to them -- every row, not the capped few the profile
 * read carries.
 */
function useHistory(key, list, studentId) {
  const [page, setPage] = useState(1);
  const params = { student_id: studentId, page, page_size: HISTORY_PAGE_SIZE };
  const query = useQuery({
    queryKey: [key, params],
    queryFn: () => list(params),
    placeholderData: keepPreviousData,
  });
  return { ...query, rows: query.data?.data ?? [], meta: query.data?.meta, setPage };
}

function HistoryCard({ title, history, emptyTitle, children }) {
  const { rows, meta, isPending, isError, error, refetch, setPage } = history;
  return (
    <Card>
      <CardHeader
        title={title}
        description={meta?.total_items ? `${meta.total_items} in total, newest first.` : undefined}
      />
      {isError ? (
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      ) : isPending ? (
        <LoadingState label={`Loading ${title.toLowerCase()}…`} />
      ) : rows.length === 0 ? (
        <EmptyState compact title={emptyTitle} />
      ) : (
        <>
          <ol className="divide-line divide-y">{rows.map(children)}</ol>
          {meta && meta.total_pages > 1 && (
            <Pagination
              className="mt-3"
              page={meta.page}
              pageSize={HISTORY_PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </Card>
  );
}

function ApplicationHistory({ studentId }) {
  const history = useHistory('admin-student-applications', adminApi.listApplications, studentId);
  return (
    <HistoryCard
      title="Applications"
      history={history}
      emptyTitle="This student has not applied to anything"
    >
      {(row) => (
        <li key={row.id} className="flex flex-wrap items-start justify-between gap-2 py-3">
          <div className="min-w-0">
            <Link
              to={paths.admin.applicationDetail(row.id)}
              className="text-ink text-sm font-medium hover:underline"
            >
              {row.internship_title}
            </Link>
            <Link
              to={paths.admin.companyDetail(row.company_id)}
              className="text-ink-muted block text-xs hover:underline"
            >
              {row.company_name}
            </Link>
            <p className="text-ink-muted text-xs">
              Applied {formatDate(row.created_at)}
              {row.decided_at ? ` · decided ${formatDate(row.decided_at)}` : ''}
            </p>
          </div>
          <Badge tone={APPLICATION_TONES[row.status] ?? 'neutral'}>
            {APPLICATION_LABELS[row.status] ?? humanizeStatus(row.status)}
          </Badge>
        </li>
      )}
    </HistoryCard>
  );
}

function InterviewHistory({ studentId }) {
  const history = useHistory('admin-student-interviews', adminApi.listInterviews, studentId);
  return (
    <HistoryCard title="Interviews" history={history} emptyTitle="No interviews">
      {(row) => (
        <li key={row.id} className="flex flex-wrap items-start justify-between gap-2 py-3">
          <div className="min-w-0">
            <Link
              to={paths.admin.applicationDetail(row.application_id)}
              className="text-ink text-sm font-medium hover:underline"
            >
              Round {row.round_number} · {row.title}
            </Link>
            <p className="text-ink-muted text-xs">
              {row.company_name} · {row.internship_title}
            </p>
            <p className="text-ink-muted text-xs">
              {formatDate(row.scheduled_at)} · {humanizeStatus(row.mode)}
            </p>
          </div>
          <Badge tone={INTERVIEW_TONES[row.status] ?? 'neutral'}>
            {INTERVIEW_LABELS[row.status] ?? humanizeStatus(row.status)}
          </Badge>
        </li>
      )}
    </HistoryCard>
  );
}

function OfferHistory({ studentId }) {
  const history = useHistory('admin-student-offers', adminApi.listOffers, studentId);
  return (
    <HistoryCard title="Offers" history={history} emptyTitle="No offers">
      {(row) => (
        <li key={row.id} className="flex flex-wrap items-start justify-between gap-2 py-3">
          <div className="min-w-0">
            <Link
              to={paths.admin.applicationDetail(row.application_id)}
              className="text-ink text-sm font-medium hover:underline"
            >
              {row.title}
            </Link>
            <p className="text-ink-muted text-xs">
              {row.company_name} · {row.internship_title}
            </p>
            <p className="text-ink-muted text-xs">
              Starts {formatDate(row.starts_on)}
              {row.sent_at ? ` · sent ${formatDate(row.sent_at)}` : ' · not sent yet'}
            </p>
          </div>
          <Badge tone={OFFER_TONES[row.status] ?? 'neutral'}>
            {OFFER_LABELS[row.status] ?? humanizeStatus(row.status)}
          </Badge>
        </li>
      )}
    </HistoryCard>
  );
}
